import React from "react";
import {
  Controller,
  FieldValues,
  FieldPath,
  UseControllerProps,
} from "react-hook-form";
import { SxProps, TextFieldVariants } from "@mui/material";
import TextInput from "./TextInput";

interface CustomInputProps<TFieldValues extends FieldValues = FieldValues> {
  control: UseControllerProps<TFieldValues>["control"];
  name: FieldPath<TFieldValues>;
  rules?: UseControllerProps<TFieldValues>["rules"];
  placeholder?: string;
  secureTextEntry?: boolean;
  label?: string;
  defaultValue?: any;
  disabled?: boolean;
  sx?: SxProps;
  variant?: TextFieldVariants;
  required?: boolean;
  id?: string;
  // Applied to the typed value BEFORE the form stores it, so what the field
  // holds is what a later reader sends. A money field passes a digit-only
  // filter here; without one a decimal is accepted, then truncated by whoever
  // validates it and sent in full by whoever transmits it.
  transform?: (value: string) => string;
}

const CustomInput = <TFieldValues extends FieldValues>({
  control,
  name,
  id = "",
  rules = {},
  placeholder,
  disabled = false,
  defaultValue,
  variant = "outlined",
  required = false,
  label = "",
  transform,
  sx = { width: "100%", paddingBottom: 5 },
  ...rest
}: CustomInputProps<TFieldValues>) => {
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      defaultValue={defaultValue}
      disabled={disabled}
      {...rest}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <TextInput
          helperText={error ? error.message : null}
          error={!!error}
          data-testid={"account_number"}
          id={id}
          onChange={event =>
            transform ? onChange(transform(event.target.value)) : onChange(event)
          }
          value={value}
          size="medium"
          sx={sx}
          variant={variant}
          label={label}
          disabled={disabled}
          fullWidth
          {...rest}
        />
      )}
    />
  );
};

export default CustomInput;
